// src/pages/Lounge/MemberSearch/MemberSearch.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import api from "@/utils/axios";
import cls from "./MemberSearch.module.scss";
import avatarPng from "@/assets/UI/avatar.png";
import AdminAvatar from "@/assets/UI/admin.webp";
import chatIcon from "@/assets/UI/chat.webp";

import { GENDERS, COUNTRIES, MEMBER_TYPES } from "@/enums/MemberEnums";
import { SEXUAL_IDENTITIES, SEXUAL_ROLES } from "@/enums/lifestyleEnums";

const MEMBER_TYPE_OPTIONS = [
  { value: "", labelKey: "memberSearch.options.memberType.any" },
  ...MEMBER_TYPES.map((value) => ({
    value,
    labelKey: `memberSearch.options.memberType.${value}`,
  })),
];

const GENDER_OPTIONS = [
  { value: "", labelKey: "memberSearch.options.gender.any" },
  ...GENDERS.map((value) => ({
    value,
    labelKey: `memberSearch.options.gender.${value}`,
  })),
];

const SEXUAL_IDENTITY_OPTIONS = [
  { value: "", labelKey: "memberSearch.options.sexualIdentity.any" },
  ...SEXUAL_IDENTITIES.map((value) => ({
    value,
    labelKey: `memberSearch.options.sexualIdentity.${value}`,
  })),
];

const SEXUAL_ROLE_OPTIONS = [
  { value: "", labelKey: "memberSearch.options.sexualRole.any" },
  ...SEXUAL_ROLES.map((value) => ({
    value,
    labelKey: `memberSearch.options.sexualRole.${value}`,
  })),
];

const SEARCH_MODES = [
  "main",
  "username",
  "singles",
  "couples",
  "sexualVibe",
  "partyVibe",
];

const getOnlineAvatar = (user) => {
  const username = String(user?.username || "").trim();
  if (username === "NotyAdmin") return AdminAvatar;
  return user?.avatar?.url || avatarPng;
};

function buildMapPositions(count) {
  const presets = [
    { top: "13%", left: "16%" },
    { top: "22%", left: "42%" },
    { top: "15%", left: "70%" },
    { top: "35%", left: "84%" },
    { top: "45%", left: "22%" },
    { top: "52%", left: "50%" },
    { top: "47%", left: "72%" },
    { top: "68%", left: "14%" },
    { top: "76%", left: "38%" },
    { top: "70%", left: "62%" },
    { top: "82%", left: "82%" },
    { top: "30%", left: "58%" },
  ];

  return Array.from({ length: count }, (_, i) => presets[i % presets.length]);
}

function PresenceNode({ user, index, pos, onOpenProfile }) {
  const username = user?.username || "Unknown user";

  return (
    <div
      className={cls.mapNode}
      style={{
        top: pos.top,
        left: pos.left,
        animationDelay: `${index * 0.14}s`,
      }}
      title={username}
    >
      <button
        type="button"
        className={cls.mapNodeButton}
        onClick={onOpenProfile}
        disabled={!user?.memberId}
      >
        <span className={cls.mapNodePulse} />
        <span className={cls.mapNodeOrbit} />
        <span className={cls.mapNodeRing} />
        <img
          src={getOnlineAvatar(user)}
          alt={username}
          className={cls.mapNodeAvatar}
        />
      </button>

      <div className={cls.mapNodeLabel}>
        <span className={cls.mapNodeName}>{username}</span>
        <span className={cls.mapNodeStatus}>Live now</span>
      </div>
    </div>
  );
}

function OnlineUserCard({
  user,
  onOpenProfile,
  onOpenChat,
  disabled,
  hasMemberProfile,
  isAdmin,
}) {
  const username = user?.username || "Unknown user";
  const canOpenProfile =
    Boolean(onOpenProfile) &&
    Boolean(hasMemberProfile) &&
    !disabled &&
    !isAdmin;
  const canOpenChat =
    Boolean(onOpenChat) && Boolean(hasMemberProfile) && !disabled && !isAdmin;

  return (
    <article className={cls.onlineCard}>
      <div className={cls.onlineCardGlow} aria-hidden="true" />
      <div className={cls.onlineCardLine} aria-hidden="true" />

      <div className={cls.onlineCardMain}>
        <button
          type="button"
          className={cls.onlineAvatarBtn}
          onClick={onOpenProfile}
          disabled={!canOpenProfile}
          title="Open member profile"
        >
          <span className={cls.onlinePulse} />
          <span className={cls.onlineAvatarRing} />
          <img
            src={getOnlineAvatar(user)}
            alt={`${username} avatar`}
            className={cls.onlineAvatarImage}
          />
        </button>

        <div className={cls.onlineIdentity}>
          <button
            type="button"
            className={cls.onlineUsernameBtn}
            onClick={onOpenProfile}
            disabled={!canOpenProfile}
            title="Open member profile"
          >
            @{username}
          </button>

          <div className={cls.onlineStatusRow}>
            <span className={cls.onlineDot} />
            <span className={cls.onlineStatusText}>Available for chat</span>
          </div>
        </div>

        <button
          type="button"
          className={cls.actionBtn}
          onClick={onOpenChat}
          disabled={!canOpenChat}
          title="Open chat"
        >
          <img src={chatIcon} alt="" />
        </button>
      </div>
    </article>
  );
}

const MemberSearch = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const didInitialFetch = useRef(false);

  const [mode, setMode] = useState("main");
  const [filters, setFilters] = useState({
    country: "",
    memberType: "",
    gender: "",
    sexual_identity: "",
    sexual_role: "",
    ageMin: "",
    ageMax: "",
  });

  const [usernameTerm, setUsernameTerm] = useState("");
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [onlineUsers, setOnlineUsers] = useState([]);
  const [onlineLoading, setOnlineLoading] = useState(true);
  const [onlineError, setOnlineError] = useState("");
  const [currentMemberId, setCurrentMemberId] = useState(null);

  const mapPositions = useMemo(
    () => buildMapPositions(Math.max(onlineUsers.length, 12)),
    [onlineUsers.length],
  );

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  const handleFilterChange = (key) => (event) => {
    setFilters((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const endpointForMode = (currentMode) => {
    switch (currentMode) {
      case "username":
        return "/v1/member-search/username";
      case "singles":
        return "/v1/member-search/singles-near-me";
      case "couples":
        return "/v1/member-search/couples-near-me";
      case "sexualVibe":
        return "/v1/member-search/my-sexual-vibe";
      case "partyVibe":
        return "/v1/member-search/my-party-vibe";
      default:
        return "/v1/member-search";
    }
  };

  const buildPayload = useCallback(
    (currentMode, pageToLoad) => {
      if (currentMode === "username") {
        return { username: usernameTerm.trim(), page: pageToLoad };
      }

      if (currentMode === "main") {
        return { ...filters, page: pageToLoad };
      }

      return { page: pageToLoad };
    },
    [filters, usernameTerm],
  );

  const fetchResults = useCallback(
    async (pageToLoad = 1, replace = true, forcedMode) => {
      const currentMode = forcedMode || mode;

      try {
        setLoading(true);
        setError("");

        const { data } = await api.post(
          endpointForMode(currentMode),
          buildPayload(currentMode, pageToLoad),
        );

        const newResults = data.results || [];

        setTotal(data.total || 0);
        setHasMore(Boolean(data.hasMore));
        setPage(data.page || pageToLoad);
        setResults((prev) => (replace ? newResults : [...prev, ...newResults]));
      } catch (err) {
        console.error("Member search error", err);
        setError(t("memberSearch.messages.error"));
      } finally {
        setLoading(false);
      }
    },
    [mode, buildPayload, t],
  );

  const resolveMemberId = useCallback((user) => {
    if (!user) return null;
    if (user.memberId) return user.memberId;
    if (user.member?._id) return user.member._id;
    if (typeof user.member === "string") return user.member;
    if (user.member_id) return user.member_id;
    return null;
  }, []);

  useEffect(() => {
    if (didInitialFetch.current) return;
    didInitialFetch.current = true;
    fetchResults(1, true, "main");
  }, [fetchResults]);

  useEffect(() => {
    const fetchOnlineUsers = async () => {
      try {
        setOnlineLoading(true);
        setOnlineError("");
        const { data } = await api.get("/v1/member-online/online-users");
        setOnlineUsers(data?.users ?? []);
      } catch (err) {
        console.error("Online citizens fetch error", err);
        setOnlineUsers([]);
        setOnlineError("Failed to load online citizens.");
      } finally {
        setOnlineLoading(false);
      }
    };

    fetchOnlineUsers();
  }, []);

  useEffect(() => {
    const loadMe = async () => {
      try {
        const { data } = await api.get("/v1/auth/me");
        const me = data?.user || data;
        const memberId = me?.member?._id || me?.memberId || null;
        setCurrentMemberId(memberId);
      } catch (err) {
        console.error("Failed to load current member", err);
      }
    };

    loadMe();
  }, []);

  const showModeLabel = (key) => {
    switch (key) {
      case "main":
        return t("memberSearch.modes.main");
      case "username":
        return t("memberSearch.modes.username");
      case "singles":
        return t("memberSearch.modes.singlesNearMe");
      case "couples":
        return t("memberSearch.modes.couplesNearMe");
      case "sexualVibe":
        return t("memberSearch.modes.mySexualVibe");
      case "partyVibe":
        return t("memberSearch.modes.myPartyVibe");
      default:
        return key;
    }
  };

  const switchMode = async (nextMode) => {
    setMode(nextMode);
    setError("");

    if (nextMode === "username") {
      setResults([]);
      setTotal(0);
      setHasMore(false);
      return;
    }

    await fetchResults(1, true, nextMode);
  };

  const handleSubmitMain = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setMode("main");
    await fetchResults(1, true, "main");
    setSubmitting(false);
  };

  const handleSubmitUsername = async (event) => {
    event.preventDefault();
    if (!usernameTerm.trim()) return;

    setSubmitting(true);
    setMode("username");
    await fetchResults(1, true, "username");
    setSubmitting(false);
  };

  const handleLoadMore = () => {
    if (!hasMore || loading) return;
    fetchResults(page + 1, false);
  };

  const openProfile = (memberId) => {
    if (!memberId) return;
    navigate(`/lounge/member-profile/${memberId}`);
  };

  const openChat = (memberId) => {
    if (!memberId) return;
    navigate(`/lounge/chat/${memberId}`);
  };

  return (
    <main className={cls.page}>
      <div className={cls.pageInner}>
        <nav className={cls.tabs}>
          <Link
            to="/lounge"
            className={`${cls.tab} ${isPublic ? cls.active : ""}`}
          >
            Public
          </Link>
          <Link
            to="/lounge/friends-feed"
            className={`${cls.tab} ${isFriends ? cls.active : ""}`}
          >
            Friends
          </Link>
          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tab} ${isClubs ? cls.active : ""}`}
          >
            Clubs
          </Link>
          <Link
            to="/lounge/chat"
            className={`${cls.tab} ${isChat ? cls.active : ""}`}
          >
            Chat
          </Link>
          <Link
            to="/lounge/search"
            className={`${cls.tab} ${isSearch ? cls.active : ""}`}
          >
            🔍 Search
          </Link>
        </nav>

        <section className={cls.heroCard}>
          <div className={cls.heroGlow} aria-hidden="true" />
          <div className={cls.heroText}>
            <span className={cls.kicker}>Noty Finder</span>
            <h1>{t("memberSearch.header.title")}</h1>
            <p>{t("memberSearch.header.subtitle")}</p>
          </div>

          <div className={cls.resultBadge}>
            <strong>{total}</strong>
            <span>{t("memberSearch.results.title")}</span>
          </div>
        </section>

        <section className={cls.onlineSection} aria-label="Online users">
          <div className={cls.onlineSectionGlow} aria-hidden="true" />
          <div className={cls.onlineSparkA} aria-hidden="true" />
          <div className={cls.onlineSparkB} aria-hidden="true" />

          <header className={cls.onlineHeader}>
            <div className={cls.onlineTitleWrap}>
              <p className={cls.sectionEyebrow}>Presence monitor</p>
              <h2 className={cls.onlineTitle}>Live member constellation</h2>
              <p className={cls.onlineSubtitle}>
                See who is active inside Noty Universe right now.
              </p>
            </div>

            <div className={cls.onlineMetaBox}>
              <div className={cls.onlineCountBadge}>{onlineUsers.length}</div>
              <div className={cls.onlineCountText}>
                <span className={cls.onlineCountValue}>
                  {onlineUsers.length === 1
                    ? "1 member online"
                    : `${onlineUsers.length} members online`}
                </span>
                <span className={cls.onlineCountHint}>Real-time pulse</span>
              </div>
            </div>
          </header>

          {onlineLoading && (
            <div className={cls.statePanel}>
              <span className={cls.stateOrb} />
              <p className={cls.onlineInfo}>Scanning live presence...</p>
            </div>
          )}

          {onlineError && !onlineLoading && (
            <div className={`${cls.statePanel} ${cls.stateError}`}>
              <span className={cls.stateOrb} />
              <p className={cls.onlineError}>{onlineError}</p>
            </div>
          )}

          {!onlineLoading && !onlineError && onlineUsers.length === 0 && (
            <div className={cls.statePanel}>
              <span className={cls.stateOrb} />
              <p className={cls.onlineInfo}>
                No active members detected right now.
              </p>
            </div>
          )}

          {!onlineLoading && !onlineError && onlineUsers.length > 0 && (
            <>
              <div className={cls.mapBoard}>
                <div className={cls.mapBackdrop} />
                <div className={cls.mapGrid} />
                <div className={cls.mapScanLine} />
                <div className={cls.mapOrbA} />
                <div className={cls.mapOrbB} />
                <div className={cls.mapCore} />

                <div className={cls.mapBrand} aria-hidden="true">
                  <span className={cls.mapBrandTop}>NOTY</span>
                  <span className={cls.mapBrandBottom}>UNIVERSE</span>
                </div>

                {onlineUsers.map((user, index) => {
                  const userRole = String(user?.role || "").toLowerCase();
                  const isAdmin =
                    userRole === "admin" ||
                    String(user?.username || "").trim() === "NotyAdmin";
                  const memberId = resolveMemberId(user);

                  return (
                    <PresenceNode
                      key={user._id || `${user.username}-${index}`}
                      user={user}
                      index={index}
                      pos={mapPositions[index]}
                      onOpenProfile={() => {
                        if (isAdmin) return;
                        openProfile(memberId);
                      }}
                    />
                  );
                })}
              </div>

              <div className={cls.onlineGrid}>
                {onlineUsers.map((u) => {
                  const memberId = resolveMemberId(u);
                  const userRole = String(u?.role || "").toLowerCase();
                  const isAdmin =
                    userRole === "admin" ||
                    String(u?.username || "").trim() === "NotyAdmin";
                  const isCurrentMember =
                    memberId &&
                    currentMemberId &&
                    String(memberId) === String(currentMemberId);

                  return (
                    <OnlineUserCard
                      key={u._id || u.username}
                      user={u}
                      onOpenProfile={() => openProfile(memberId)}
                      onOpenChat={() => openChat(memberId)}
                      disabled={Boolean(isCurrentMember)}
                      hasMemberProfile={Boolean(memberId)}
                      isAdmin={isAdmin}
                    />
                  );
                })}
              </div>
            </>
          )}
        </section>

        <section className={cls.panel}>
          <div className={cls.panelHead}>
            <div>
              <p className={cls.sectionEyebrow}>Discovery controls</p>
              <h2>{showModeLabel(mode)}</h2>
              <p>{t("memberSearch.results.count", { total })}</p>
            </div>
          </div>

          <div className={cls.modeRow}>
            {SEARCH_MODES.map((key) => (
              <button
                key={key}
                type="button"
                className={`${cls.modeBtn} ${mode === key ? cls.modeActive : ""}`}
                onClick={() => switchMode(key)}
              >
                {showModeLabel(key)}
              </button>
            ))}
          </div>

          {mode === "main" && (
            <form className={cls.form} onSubmit={handleSubmitMain}>
              <div className={cls.field}>
                <label>{t("memberSearch.filters.countryLabel")}</label>
                <select
                  value={filters.country}
                  onChange={handleFilterChange("country")}
                >
                  <option value="">
                    {t("memberSearch.options.country.any")}
                  </option>
                  {COUNTRIES.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>

              <div className={cls.field}>
                <label>{t("memberSearch.filters.memberTypeLabel")}</label>
                <select
                  value={filters.memberType}
                  onChange={handleFilterChange("memberType")}
                >
                  {MEMBER_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </option>
                  ))}
                </select>
              </div>

              <div className={cls.field}>
                <label>{t("memberSearch.filters.genderLabel")}</label>
                <select
                  value={filters.gender}
                  onChange={handleFilterChange("gender")}
                >
                  {GENDER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </option>
                  ))}
                </select>
              </div>

              <div className={cls.field}>
                <label>{t("memberSearch.filters.sexualIdentityLabel")}</label>
                <select
                  value={filters.sexual_identity}
                  onChange={handleFilterChange("sexual_identity")}
                >
                  {SEXUAL_IDENTITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </option>
                  ))}
                </select>
              </div>

              <div className={cls.field}>
                <label>{t("memberSearch.filters.sexualRoleLabel")}</label>
                <select
                  value={filters.sexual_role}
                  onChange={handleFilterChange("sexual_role")}
                >
                  {SEXUAL_ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </option>
                  ))}
                </select>
              </div>

              <div className={cls.ageGrid}>
                <div className={cls.field}>
                  <label>{t("memberSearch.filters.ageMinLabel")}</label>
                  <input
                    type="number"
                    min="18"
                    max="90"
                    value={filters.ageMin}
                    onChange={handleFilterChange("ageMin")}
                    placeholder={t("memberSearch.filters.ageMinPlaceholder")}
                  />
                </div>

                <div className={cls.field}>
                  <label>{t("memberSearch.filters.ageMaxLabel")}</label>
                  <input
                    type="number"
                    min="18"
                    max="90"
                    value={filters.ageMax}
                    onChange={handleFilterChange("ageMax")}
                    placeholder={t("memberSearch.filters.ageMaxPlaceholder")}
                  />
                </div>
              </div>

              {error && <p className={cls.error}>{error}</p>}

              <button
                type="submit"
                className={cls.primaryBtn}
                disabled={submitting || loading}
              >
                {submitting || loading
                  ? t("memberSearch.actions.loading")
                  : t("memberSearch.actions.apply")}
              </button>
            </form>
          )}

          {mode === "username" && (
            <form className={cls.form} onSubmit={handleSubmitUsername}>
              <div className={cls.field}>
                <label>{t("memberSearch.filters.usernameLabel")}</label>
                <input
                  type="text"
                  value={usernameTerm}
                  onChange={(e) => setUsernameTerm(e.target.value)}
                  placeholder={t("memberSearch.filters.usernamePlaceholder")}
                />
              </div>

              {error && <p className={cls.error}>{error}</p>}

              <button
                type="submit"
                className={cls.primaryBtn}
                disabled={submitting || loading || !usernameTerm.trim()}
              >
                {submitting || loading
                  ? t("memberSearch.actions.loading")
                  : t("memberSearch.actions.searchUsername")}
              </button>
            </form>
          )}

          {mode !== "main" && mode !== "username" && (
            <p className={cls.helperText}>{t(`memberSearch.helper.${mode}`)}</p>
          )}
        </section>

        <section className={cls.panel}>
          <div className={cls.panelHead}>
            <div>
              <p className={cls.sectionEyebrow}>Matched citizens</p>
              <h2>{t("memberSearch.results.title")}</h2>
              <p>{t("memberSearch.results.subtitle")}</p>
            </div>
          </div>

          {loading && results.length === 0 && (
            <p className={cls.infoText}>{t("memberSearch.messages.loading")}</p>
          )}

          {!loading && results.length === 0 && (
            <div className={cls.emptyState}>
              <span>🔎</span>
              <p>{t("memberSearch.messages.empty")}</p>
            </div>
          )}

          <div className={cls.resultsGrid}>
            {results.map((m) => {
              const profileId = m.id;
              const avatarUrl = m.avatar?.url || avatarPng;

              return (
                <article key={m.id} className={cls.memberCard}>
                  <div className={cls.cardGlow} aria-hidden="true" />

                  <div className={cls.avatar}>
                    <img
                      src={avatarUrl}
                      alt={t("memberSearch.results.avatarAlt")}
                    />
                    {m.isOnline && <span className={cls.onlineDot} />}
                  </div>

                  <div className={cls.memberInfo}>
                    <div className={cls.nameRow}>
                      <strong>@{m.username || "member"}</strong>
                      {m.memberType && (
                        <span>
                          {t(`memberSearch.options.memberType.${m.memberType}`)}
                        </span>
                      )}
                    </div>

                    <p>
                      {m.city && m.country
                        ? `${m.city}, ${m.country}`
                        : m.country ||
                          t("memberSearch.results.locationUnknown")}
                    </p>
                  </div>

                  <Link
                    to={`/lounge/member-profile/${profileId}`}
                    className={cls.viewBtn}
                  >
                    {t("memberSearch.results.viewProfile")}
                  </Link>
                </article>
              );
            })}
          </div>

          {hasMore && (
            <div className={cls.loadMore}>
              <button
                type="button"
                className={cls.secondaryBtn}
                disabled={loading}
                onClick={handleLoadMore}
              >
                {loading
                  ? t("memberSearch.actions.loadingMore")
                  : t("memberSearch.actions.loadMore")}
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default MemberSearch;
